import { useEffect, useState } from 'react'

import { formatDistanceToNowStrict } from 'date-fns'

const ONE_SECOND_MS = 1000
const ONE_MINUTE_MS = 60 * ONE_SECOND_MS

const formatRelativeTime = (timestamp: string): string =>
  formatDistanceToNowStrict(new Date(timestamp), { addSuffix: true })

/**
 * Relative "x ago" label for a timestamp. Ticks on its own schedule — every
 * second while the timestamp is under a minute old, then once a minute after
 * that — so the label stays current without depending on the caller to
 * re-render (e.g. hover state elsewhere in the tree).
 */
export const useRelativeTime = (timestamp?: string | null): string | null => {
  const [label, setLabel] = useState<string | null>(() =>
    timestamp ? formatRelativeTime(timestamp) : null,
  )

  useEffect(() => {
    if (!timestamp) {
      setLabel(null)
      return
    }

    let timeoutId: number

    const tick = (): void => {
      setLabel(formatRelativeTime(timestamp))

      const elapsedMs = Math.abs(Date.now() - new Date(timestamp).getTime())
      const delayMs = elapsedMs < ONE_MINUTE_MS ? ONE_SECOND_MS : ONE_MINUTE_MS
      timeoutId = window.setTimeout(tick, delayMs)
    }

    tick()

    return (): void => {
      window.clearTimeout(timeoutId)
    }
  }, [timestamp])

  return label
}
